import React, { useState, useEffect, useRef } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Badge from "@react/shared/ui/Badge"

const STATUS_TONE = {
  draft:    "neutral",
  active:   "success",
  inactive: "warning",
}

const StatusMenu = ({ contract, statuses, updatePath, canEdit }) => {
  const [status, setStatus] = useState(contract.status)
  const [label,  setLabel]  = useState(contract.status_label)
  const [open,   setOpen]   = useState(false)
  const [saving, setSaving] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const pick = async (next) => {
    if (next.value === status) { setOpen(false); return }
    const prevStatus = status
    const prevLabel  = label
    setStatus(next.value)
    setLabel(next.label)
    setOpen(false)
    setSaving(true)
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content
      const res  = await fetch(updatePath, {
        method:  "PATCH",
        headers: {
          "Content-Type":     "application/json",
          "X-CSRF-Token":     csrf,
          "Accept":           "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        body:        JSON.stringify({ mbc_contract: { status: next.value } })
      })
      if (res.ok) return
      setStatus(prevStatus)
      setLabel(prevLabel)
    } catch {
      setStatus(prevStatus)
      setLabel(prevLabel)
    } finally {
      setSaving(false)
    }
  }

  if (!canEdit || !updatePath) {
    return (
      <Badge tone={STATUS_TONE[status] || "neutral"} variant="soft" size="sm">
        {label}
      </Badge>
    )
  }

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type      = "button"
        onClick   = {() => setOpen(o => !o)}
        disabled  = {saving}
        className = {`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold ring-1 ring-inset cursor-pointer transition ${
          STATUS_TONE[status] === "success" ? "bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100" :
          STATUS_TONE[status] === "warning" ? "bg-amber-50 text-amber-700 ring-amber-200 hover:bg-amber-100" :
                                              "bg-slate-50 text-slate-700 ring-slate-200 hover:bg-slate-100"
        } ${saving ? "opacity-60" : ""}`}
      >
        {label}
        <FontAwesomeIcon icon={saving ? "circle-notch" : "chevron-down"} className={`text-[9px] ${saving ? "animate-spin" : ""}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-30 w-44 bg-white border border-slate-200 rounded-md shadow-lg py-1">
          {statuses.map(opt => (
            <button
              key       = {opt.value}
              type      = "button"
              onClick   = {() => pick(opt)}
              className = {`flex items-center justify-between w-full px-3 py-1.5 text-sm text-left text-slate-700 hover:bg-slate-50 ${opt.value === status ? "font-semibold" : ""}`}
            >
              <span>{opt.label}</span>
              {opt.value === status && (
                <FontAwesomeIcon icon="check" className="text-emerald-500 text-[11px]" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const Meta = ({ icon, label, link, fallback = "—" }) => {
  const content = label || fallback
  return (
    <span className="inline-flex items-center gap-1.5 text-[13px] text-slate-600">
      <FontAwesomeIcon icon={icon} className="text-slate-400 text-[12px]" />
      {link
        ? <a href={link} className="hover:text-slate-900 hover:underline">{content}</a>
        : <span>{content}</span>}
    </span>
  )
}

const Header = ({ contract, paths, policies, statuses }) => (
  <div className="bg-white border-b border-slate-200">
    <div className="px-8 pt-6 pb-5">
      <div className="flex items-start justify-between gap-4">
        <a
          href      = {paths.back}
          className = "inline-flex items-center justify-center w-8 h-8 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700 shrink-0 mt-1"
          title     = {I18n.t("button.back", { defaultValue: "Retour" })}
        >
          <FontAwesomeIcon icon="arrow-left" className="text-[13px]" />
        </a>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight truncate">
              {contract.show_title || contract.title || contract.code}
            </h1>
            <StatusMenu
              contract   = {contract}
              statuses   = {statuses || []}
              updatePath = {paths.update}
              canEdit    = {policies.can_edit}
            />
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
            {contract.partner_name && (
              <Meta icon="handshake" label={contract.partner_name} />
            )}
            <Meta icon="user-circle" label={contract.client?.name} link={contract.client?.path} fallback="Sans client" />
            <Meta icon="calendar"    label={`${contract.start_date_display || "—"}${contract.end_date_display ? ` → ${contract.end_date_display}` : ""}`} />
            {contract.comment && (
              <Meta icon="message" label={contract.comment} />
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {paths.duplicate && (
            <a
              href      = {paths.duplicate}
              className = "inline-flex items-center gap-2 h-9 px-3 rounded-md bg-white border border-slate-200 text-[13px] font-medium text-slate-700 hover:bg-slate-50"
            >
              <FontAwesomeIcon icon="copy" className="text-[12px]" />
              {I18n.t("quotes.bigcard.duplicate", { defaultValue: "Dupliquer" })}
            </a>
          )}
          {paths.edit && (
            <a
              href      = {paths.edit}
              className = "inline-flex items-center gap-2 h-9 px-3 rounded-md bg-brand-500 text-[13px] font-medium text-white hover:bg-brand-600"
            >
              <FontAwesomeIcon icon="pen" className="text-[12px]" />
              {I18n.t("button.edit", { defaultValue: "Modifier" })}
            </a>
          )}
        </div>
      </div>
    </div>
  </div>
)

export default Header
